import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import outlineData from './data/puglia-outline.json';
import { nodiMappa } from './comuni';
import type { NodoMappa } from './comuni';
import { useComuni } from './useComuni';
import styles from './Constellation.module.css';

/**
 * Mappa a "costellazione" dei comuni — SVG DECORATIVO.
 * ---------------------------------------------------------------------------
 * Con l'allineamento visual-diff alla home del riferimento (29/07/2026) la
 * mappa vive DENTRO l'hero, colonna destra, e l'elenco navigabile dei comuni
 * non sta più accanto alla mappa: è nel dropdown «Comuni» dell'header
 * (decisione del committente, 29/07/2026), che è l'alternativa testuale
 * richiesta da WCAG 1.1.1 — link reali raggruppati per provincia, percorsi in
 * 6 passi invece di centinaia di nodi SVG da tastiera.
 *
 * L'SVG resta `aria-hidden`: nessun elemento focalizzabile dentro.
 *
 * `provinciaAttiva` accende i nodi di una provincia: è il legame visivo col
 * picker dell'hero (le pill provincia), nei due sensi ma solo decorativo.
 *
 * I punti arrivano dall'API (01/09/2026), non più da `comuni-nodes.json`
 * compilato nel bundle. Conseguenze volute:
 *  · un comune **senza** `x`/`y` non si disegna (`nodiMappa` lo esclude):
 *    metterlo a (0,0) sarebbe un punto vero in un posto falso;
 *  · con l'elenco vuoto, in caricamento o in errore resta la SAGOMA e basta.
 *    La sagoma è geografia, non un dato del portale: non sparisce perché
 *    l'API non risponde, e non c'è nessuno scheletro che gira all'infinito.
 * La didascalia dice quale dei tre casi è: è il testo che sente chi non
 * vede la figura.
 */

/** Soglia di prossimità per i segmenti, ripresa dall'originale (TH = 58). */
const LINK_THRESHOLD = 58;

const NODI_VUOTI: readonly NodoMappa[] = [];

export interface ConstellationMapProps {
  /** Se `false` non disegna i segmenti: usato in kiosk per alleggerire. */
  readonly showLinks?: boolean;
  /** Sigla provincia da evidenziare (dal picker dell'hero), o null. */
  readonly provinciaAttiva?: string | null;
}

export function ConstellationMap({
  showLinks = true,
  provinciaAttiva = null,
}: ConstellationMapProps) {
  const { t } = useTranslation();
  const stato = useComuni();

  /**
   * `x`/`y` sono unità del `viewBox` della sagoma, e il server DICHIARA quale
   * (`meta.viewbox`). Se dichiara un riferimento diverso da quello della
   * sagoma che stiamo disegnando, i punti non sono collocabili qui: si
   * omettono e la didascalia lo dice. Disegnarli comunque metterebbe 257
   * punti veri in posti sbagliati, che è peggio del non disegnarli.
   * `null` = non dichiarato (fixture): non c'è niente da confrontare.
   */
  const riferimentoCoerente =
    stato.status !== 'success' ||
    stato.data.viewbox === null ||
    stato.data.viewbox === outlineData.viewBox;

  const nodes = useMemo(
    () =>
      stato.status === 'success' && riferimentoCoerente
        ? nodiMappa(stato.data.nodi)
        : NODI_VUOTI,
    [riferimentoCoerente, stato],
  );

  const links = useMemo(() => {
    if (!showLinks) return [];
    const result: { x1: number; y1: number; x2: number; y2: number }[] = [];
    for (let i = 0; i < nodes.length; i += 1) {
      for (let j = i + 1; j < nodes.length; j += 1) {
        const a = nodes[i]!;
        const b = nodes[j]!;
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        if (dx * dx + dy * dy < LINK_THRESHOLD * LINK_THRESHOLD) {
          result.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
        }
      }
    }
    return result;
  }, [nodes, showLinks]);

  return (
    <figure className={styles.figure}>
      <svg
        className={styles.svg}
        viewBox={outlineData.viewBox}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
        focusable="false"
      >
        {outlineData.paths.map((d, index) => (
          <path key={index} d={d} className={styles.outline} />
        ))}
        {links.map((link, index) => (
          <line
            key={index}
            x1={link.x1}
            y1={link.y1}
            x2={link.x2}
            y2={link.y2}
            className={styles.link}
          />
        ))}
        {nodes.map((node) => (
          <circle
            key={node.slug}
            cx={node.x}
            cy={node.y}
            r={node.capoluogo ? 7 : 4}
            className={[
              styles.node,
              node.capoluogo ? styles.nodeCapoluogo : null,
              provinciaAttiva && node.provincia === provinciaAttiva
                ? styles.nodeActive
                : null,
            ]
              .filter(Boolean)
              .join(' ')}
          />
        ))}
      </svg>
      <figcaption className="ic-sr-only">
        {riferimentoCoerente
          ? didascalia(stato.status, nodes.length, t)
          : t(
              'map_viewbox_mismatch',
              "Sagoma della Puglia. Le posizioni dei comuni sono espresse in un sistema di riferimento diverso da questa sagoma e non vengono disegnate. L'elenco dei comuni è nel menu «Comuni» dell'intestazione.",
            )}
      </figcaption>
    </figure>
  );
}

/**
 * La didascalia dice quello che la figura mostra DAVVERO.
 *
 * Prima annunciava «un punto per ciascuno dei 257 comuni» perché i 257 punti
 * erano nel bundle e c'erano sempre. Ora il numero è quello disegnato: se i
 * comuni sono zero, o senza proiezione, la figura è la sola sagoma e la
 * didascalia lo dice invece di promettere punti che non ci sono.
 */
function didascalia(
  status: 'loading' | 'success' | 'notFound' | 'error',
  quanti: number,
  t: TFunction,
): string {
  if (status === 'loading') {
    return t('map_loading', 'Mappa della Puglia: caricamento dei comuni in corso.');
  }
  if (status === 'error') {
    return t(
      'map_unavailable',
      "Mappa della Puglia: l'elenco dei comuni non è disponibile in questo momento. I comuni restano nel menu «Comuni» dell'intestazione.",
    );
  }
  if (quanti === 0) {
    return t(
      'map_empty',
      'Sagoma della Puglia. Nessun comune da mostrare sulla mappa.',
    );
  }
  return t('map_text_alternative', {
    count: quanti,
    defaultValue:
      "Mappa decorativa della Puglia con un punto per ciascuno dei {{count}} comuni collocati. L'elenco navigabile dei comuni per provincia è nel menu «Comuni» dell'intestazione.",
  });
}
